import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import {
  DELETE_CLIENT_EMPLOYEES_JOBS,
  FETCH_CLIENT_EMPLOYEES,
  FETCH_CLIENT_EMPLOYEES_JOBS,
  FETCH_CLIENT_JOBS_LIST,
  SAVE_CLIENT_EMPLOYEES,
  SAVE_CLIENT_EMPLOYEES_JOBS,
  SEND_CLIENT_INVITATION
} from "../../constant/Apipath";
import { showErrorToast, showSuccessToast } from "../../toast/toast";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import ClearIcon from "@mui/icons-material/Clear";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PublicIcon from "@mui/icons-material/Public";
import { Getrequestcall } from "../../request/Getrequest";

const defaultNrOfRecords = 20;
const jobListPageSize = 50;

const ToggleSwitch = styled(Switch)(({ theme }) => ({
  width: 52,
  height: 30,
  padding: 0,
  display: "flex",
  alignItems: "center",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#1E88E5",
        opacity: 1
      }
    }
  },
  "& .MuiSwitch-thumb": {
    width: 26,
    height: 26,
    boxShadow: "none"
  },
  "& .MuiSwitch-track": {
    borderRadius: 30,
    backgroundColor: "#CFD8DC",
    opacity: 1
  }
}));

// Helper component for Detail Rows
function DetailRow({ icon, label, value }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          mt: 0.5
        }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 500, display: "block" }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#1a202c", wordBreak: "break-all" }}>
          {value || "N/A"}
        </Typography>
      </Box>
    </Stack>
  );
}

function Employees() {
  const { loginData, selectedClientData } = useSelector((state) => state?.main || {});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const clientId = selectedClientData?.ClientId;
  const loginUserId = loginData?.userInformation?.UserId;

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeJobs, setEmployeeJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employeeJobsLoading, setEmployeeJobsLoading] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  const [allJobsLoading, setAllJobsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [jobSearchTerm, setJobSearchTerm] = useState("");
  const [debouncedJobSearchTerm, setDebouncedJobSearchTerm] = useState("");
  const [jobPage, setJobPage] = useState(1);
  const [jobHasMore, setJobHasMore] = useState(true);
  const [isFetchingMoreJobs, setIsFetchingMoreJobs] = useState(false);
  const jobListRef = useRef(null);
  const [jobToggleLoadingId, setJobToggleLoadingId] = useState(null);
  const [employeePage, setEmployeePage] = useState(1);
  const [isFetchingMoreEmployees, setIsFetchingMoreEmployees] = useState(false);
  const [employeeHasMore, setEmployeeHasMore] = useState(true);
  const [employeeTotalRecords, setEmployeeTotalRecords] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0: Details, 1: Jobs
  const employeesListRef = useRef(null);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed === debouncedSearchTerm) return;
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(trimmed);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedJobSearchTerm(jobSearchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [jobSearchTerm]);

  // Add Employee State
  const [addOpen, setAddOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    personalEmail: ""
  });
  const [savingEmployee, setSavingEmployee] = useState(false);

  const accessToken = loginData?.accessToken;
  const getEmployeeId = useCallback(
    (employee) => employee?.clientEmployeeId || employee?.id || employee?.ClientEmployeeId,
    []
  );
  const selectedEmployeeId = useMemo(
    () => getEmployeeId(selectedEmployee),
    [getEmployeeId, selectedEmployee]
  );
  const getJobId = useCallback(
    (job) => job?.clientJobId || job?.jobId || job?.JobId || job?.id || job?.ClientJobId,
    []
  );
  const employeeJobMap = useMemo(() => {
    const map = new Map();
    employeeJobs.forEach((assignment) => {
      const jobId = getJobId(assignment) || assignment?.jobId || assignment?.clientJobId;
      if (jobId) {
        map.set(jobId, assignment);
      }
    });
    return map;
  }, [employeeJobs, getJobId]);

  // --- API FUNCTIONS ---

  const buildEmployeesPayload = useCallback(
    (searchText = "", page = 1) => ({
      PageNr: page,
      NrOfRecPerPage: defaultNrOfRecords,
      FullSearch: searchText,
      SearchList: [{ ClientId: clientId, deleted: false }],
      SortList: [{ FieldName: "id", Direction: "DESC" }],
      IncludeRecordNr: true,
      ReturnRecordError: true
    }),
    [clientId]
  );

  const fetchEmployees = useCallback(
    async (searchText = "", page = 1, append = false) => {
      if (!clientId) return;
      if (append) {
        setIsFetchingMoreEmployees(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await Postrequestcall(
          FETCH_CLIENT_EMPLOYEES,
          buildEmployeesPayload(searchText, page),
          accessToken
        );
        if (response?.status === 200) {
          const list = response?.data?.data || [];
          const totalRecords =
            response?.data?.nrOfRecords ?? response?.data?.RecordNr ?? list.length;
          if (append) {
            setEmployees((prev) => {
              const next = [...prev, ...list];
              setEmployeeHasMore(next.length < totalRecords);
              return next;
            });
          } else {
            setEmployees(list);
            setEmployeeHasMore(list.length < totalRecords);
          }
          setEmployeePage(page);
        } else {
          showErrorToast("Unable to load employees.");
        }
      } catch (error) {
        console.error("fetchEmployees->", error);
        showErrorToast("Unable to load employees.");
      } finally {
        if (append) {
          setIsFetchingMoreEmployees(false);
        } else {
          setLoading(false);
        }
      }
    },
    [accessToken, buildEmployeesPayload, clientId]
  );

  const fetchEmployeeJobs = useCallback(
    async (employeeId) => {
      if (!employeeId || !clientId) return;
      setEmployeeJobsLoading(true);
      try {
        const payload = {
          PageNr: 1,
          NrOfRecPerPage: 50,
          FullSearch: "",
          SearchList: [{ ClientId: clientId, deleted: false, clientEmployeeId: employeeId }],
          SortList: [{ FieldName: "isSelected", Direction: "DESC" }],
          IncludeRecordNr: true
        };
        const response = await Postrequestcall(FETCH_CLIENT_EMPLOYEES_JOBS, payload, accessToken);
        if (response?.status === 200) {
          setEmployeeJobs(response?.data?.data || []);
        }
      } catch (error) {
        console.error("fetchEmployeeJobs->", error);
      } finally {
        setEmployeeJobsLoading(false);
      }
    },
    [accessToken, clientId]
  );

  const buildJobsListPayload = useCallback(
    (page = 1, searchText = "") => ({
      PageNr: page,
      NrOfRecPerPage: jobListPageSize,
      FullSearch: searchText,
      SearchList: [
        {
          ClientId: clientId,
          deleted: false
        }
      ],
      SortList: [{ FieldName: "id", Direction: "ASC" }],
      IncludeRecordNr: true,
      ReturnRecordError: true
    }),
    [clientId]
  );

  const fetchJobsList = useCallback(
    async (page = 1, append = false, searchText = "") => {
      if (!clientId) {
        setAllJobs([]);
        setJobPage(1);
        setJobHasMore(true);
        setAllJobsLoading(false);
        setIsFetchingMoreJobs(false);
        return;
      }
      if (append) {
        setIsFetchingMoreJobs(true);
      } else {
        setAllJobsLoading(true);
      }
      try {
        const response = await Postrequestcall(
          FETCH_CLIENT_JOBS_LIST,
          buildJobsListPayload(page, searchText),
          accessToken
        );
        if (response?.status === 200) {
          const list = response?.data?.data || [];
          const totalRecords =
            response?.data?.nrOfRecords ?? response?.data?.RecordNr ?? list.length;
          if (append) {
            setAllJobs((prev) => {
              const next = [...prev, ...list];
              setJobHasMore(next.length < totalRecords);
              return next;
            });
          } else {
            setAllJobs(list);
            setJobHasMore(list.length < totalRecords);
          }
          setJobPage(page);
        } else {
          showErrorToast("Unable to load jobs.");
        }
      } catch (error) {
        console.error("fetchJobsList->", error);
        showErrorToast("Unable to load jobs.");
      } finally {
        if (append) {
          setIsFetchingMoreJobs(false);
        } else {
          setAllJobsLoading(false);
        }
      }
    },
    [accessToken, buildJobsListPayload, clientId]
  );

  const handleJobToggle = useCallback(
    async (job, enable) => {
      const employeeId = selectedEmployeeId;
      if (!employeeId || !loginUserId) {
        showErrorToast("Select an employee first.");
        return;
      }
      const jobId = getJobId(job);
      if (!jobId) return;
      setJobToggleLoadingId(jobId);
      try {
        if (enable) {
          const payload = {
            UserId: loginUserId,
            ReturnRecordError: true,
            ReturnRecordId: true,
            SaveList: [
              {
                Id: 0,
                ClientId: clientId,
                ClientEmployeeId: employeeId,
                ClientJobId: jobId,
                ModifiedBy: loginUserId
              }
            ]
          };
          const response = await Postrequestcall(SAVE_CLIENT_EMPLOYEES_JOBS, payload, accessToken);
          if (response?.status === 200 || response?.status === 201) {
            showSuccessToast("Job enabled.");
          } else {
            showErrorToast("Unable to enable job.");
          }
        } else {
          const assignment = employeeJobMap.get(jobId);
          const assignmentId = assignment?.clientEmployeeJobId ?? assignment?.id;
          if (!assignmentId) {
            showErrorToast("Unable to disable job assignment.");
            return;
          }
          const payload = {
            UserId: loginUserId,
            ReturnRecordError: true,
            SoftDelete: true,
            DeleteList: [
              {
                Id: assignmentId,
                ClientId: clientId
              }
            ]
          };
          const response = await Postrequestcall(
            DELETE_CLIENT_EMPLOYEES_JOBS,
            payload,
            accessToken
          );
          if (response?.status === 200 || response?.status === 201) {
            showSuccessToast("Job disabled.");
          } else {
            showErrorToast("Unable to disable job.");
          }
        }
      } catch (error) {
        console.error("handleJobToggle->", error);
        showErrorToast("Unable to update job assignment.");
      } finally {
        setJobToggleLoadingId(null);
      }
      if (employeeId) {
        await fetchEmployeeJobs(employeeId);
      }
    },
    [
      accessToken,
      clientId,
      fetchEmployeeJobs,
      getJobId,
      employeeJobMap,
      loginUserId,
      selectedEmployeeId
    ]
  );

  const handleSelectEmployee = useCallback(
    (employee, isInitial = false) => {
      setSelectedEmployee(employee);
      setActiveTab(0); // Reset to Details tab
      const empId = getEmployeeId(employee);
      fetchEmployeeJobs(empId);
    },
    [fetchEmployeeJobs, getEmployeeId]
  );

  useEffect(() => {
    if (!clientId) {
      setEmployees([]);
      setEmployeePage(1);
      setEmployeeHasMore(true);
      return;
    }
    setEmployeeHasMore(true);
    setEmployeePage(1);
    fetchEmployees(debouncedSearchTerm, 1, false);
  }, [clientId, debouncedSearchTerm, fetchEmployees]);

  useEffect(() => {
    if (!selectedEmployee && employees.length > 0) {
      handleSelectEmployee(employees[0], true);
    }
  }, [employees, selectedEmployee, handleSelectEmployee]);

  const handleEmployeeListScroll = useCallback(
    (event) => {
      const target = event.currentTarget;
      if (!target) return;
      if (!employeeHasMore || loading || isFetchingMoreEmployees) return;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
      if (nearBottom) {
        fetchEmployees(debouncedSearchTerm, employeePage + 1, true);
      }
    },
    [
      employeeHasMore,
      loading,
      isFetchingMoreEmployees,
      fetchEmployees,
      debouncedSearchTerm,
      employeePage
    ]
  );

  const handleJobListScroll = useCallback(
    (event) => {
      const target = event.currentTarget;
      if (!target || !jobHasMore || allJobsLoading || isFetchingMoreJobs) return;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
      if (nearBottom) {
        fetchJobsList(jobPage + 1, true, debouncedJobSearchTerm);
      }
    },
    [jobHasMore, allJobsLoading, isFetchingMoreJobs, fetchJobsList, jobPage, debouncedJobSearchTerm]
  );

  useEffect(() => {
    if (!clientId) {
      setAllJobs([]);
      setJobPage(1);
      setJobHasMore(true);
      return;
    }
    setJobHasMore(true);
    setJobPage(1);
    fetchJobsList(1, false, debouncedJobSearchTerm);
  }, [clientId, debouncedJobSearchTerm, fetchJobsList]);

  // --- CREATE EMPLOYEE & INVITATION ---

  const handleCreateEmployee = async () => {
    if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.personalEmail) {
      showErrorToast("First Name, Last Name, and Email are required.");
      return;
    }

    setSavingEmployee(true);
    try {
      // 1. Save Employee
      const savePayload = {
        SaveList: [
          {
            Id: 0,
            ClientId: clientId,
            FirstName: newEmployee.firstName,
            LastName: newEmployee.lastName,
            PersonalEmail: newEmployee.personalEmail,
            CompanyEmail: "",
            EmployeeCode: "",
            Active: true,
            HideBiFree: false,
            SupervisorEmployeeId: 0,
            Address1: "",
            Address2: "",
            City: "",
            PostalCode: "",
            Province: "",
            Country: "",
            ModifiedBy: loginUserId
          }
        ],
        ReturnRecordError: true,
        UserId: loginUserId
      };

      const response = await Postrequestcall(SAVE_CLIENT_EMPLOYEES, savePayload, accessToken);

      if (response?.status === 200 || response?.status === 201) {
        showSuccessToast("Employee created successfully.");

        // 2. Send Invitation
        try {
          const invitationUrl = `${SEND_CLIENT_INVITATION}/${clientId}/${newEmployee.personalEmail}`;
          await Getrequestcall(invitationUrl, accessToken);
          showSuccessToast("Invitation sent to " + newEmployee.personalEmail);
        } catch (invError) {
          console.error("Invitation error->", invError);
          showErrorToast("Employee saved, but failed to send invitation.");
        }

        setAddOpen(false);
        setNewEmployee({ firstName: "", lastName: "", personalEmail: "" });
        fetchEmployees(debouncedSearchTerm, 1, false); // Refresh list
      } else {
        showErrorToast("Unable to save employee.");
      }
    } catch (error) {
      console.error("handleCreateEmployee->", error);
      showErrorToast("Unable to save employee.");
    } finally {
      setSavingEmployee(false);
    }
  };

  // --- HELPERS ---

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || "";
    const last = lastName?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const fullName = useMemo(() => {
    if (!selectedEmployee) return "";
    return `${selectedEmployee?.firstName || ""} ${selectedEmployee?.lastName || ""}`.trim();
  }, [selectedEmployee]);

  const status = selectedEmployee?.active ?? selectedEmployee?.Active ?? true;

  return (
    <Box sx={{ height: "100%", display: "flex", overflow: "hidden", gap: 3 }}>
      {/* ========== LEFT PANEL: EMPLOYEES LIST ========== */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: "100%", md: 340 },
          flexShrink: 0,
          borderRadius: 3,
          border: "1px solid #E0E0E0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}>
        {/* Header */}
        <Box sx={{ p: 2.5, borderBottom: "1px solid #E0E0E0" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Employee
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
              Add
            </Button>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setDebouncedSearchTerm(searchTerm.trim());
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <ClearIcon
                    sx={{ cursor: "pointer", fontSize: 18, color: "#9e9e9e" }}
                    onClick={() => {
                      setSearchTerm("");
                      setDebouncedSearchTerm("");
                    }}
                  />
                </InputAdornment>
              )
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#F5F7FA" } }}
          />
        </Box>

        {/* List */}
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <List
            disablePadding
            sx={{ overflowY: "auto", flex: 1 }}
            ref={employeesListRef}
            onScroll={handleEmployeeListScroll}>
            {employees.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No employees found
                </Typography>
              </Box>
            ) : (
              employees.map((emp) => {
                const isSelected = selectedEmployee?.clientEmployeeId === emp?.clientEmployeeId;
                const isActive = emp?.active ?? emp?.Active ?? true;
                const name = `${emp?.firstName || ""} ${emp?.lastName || ""}`.trim();

                return (
                  <ListItemButton
                    key={emp?.clientEmployeeId || emp?.id}
                    selected={isSelected}
                    onClick={() => handleSelectEmployee(emp)}
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid #F0F0F0",
                      "&.Mui-selected": {
                        bgcolor: "rgba(33, 150, 243, 0.08)",
                        borderLeft: "4px solid #2196F3",
                        "&:hover": { bgcolor: "rgba(33, 150, 243, 0.12)" }
                      },
                      borderLeft: "4px solid transparent"
                    }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: isSelected ? "primary.main" : "#E0E0E0",
                          color: isSelected ? "white" : "#757575",
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                        {getInitials(emp?.firstName, emp?.lastName)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: isSelected ? 700 : 600 }}>
                          {name || "Unnamed"}
                        </Typography>
                      }
                      secondary={
                        <Box
                          component="span"
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mt={0.5}>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", textTransform: "lowercase" }}>
                            {emp?.personalEmail || emp?.PersonalEmail || "No Email"}
                          </Typography>
                          <Chip
                            label={isActive ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 700,
                              bgcolor: isActive ? "#E8F5E9" : "#FFEBEE",
                              color: isActive ? "#2E7D32" : "#C62828"
                            }}
                          />
                        </Box>
                      }
                      secondaryTypographyProps={{ component: "div" }}
                    />
                  </ListItemButton>
                );
              })
            )}
            {isFetchingMoreEmployees && (
              <Box sx={{ py: 1.5, textAlign: "center" }}>
                <CircularProgress size={18} />
              </Box>
            )}
          </List>
        )}
      </Paper>

      {/* ========== RIGHT PANEL: EMPLOYEE DETAILS ========== */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 3,
          border: "1px solid #E0E0E0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}>
        {selectedEmployee ? (
          <Stack sx={{ height: "100%" }}>
            {/* Header Info */}
            <Box sx={{ p: 3, borderBottom: "1px solid #E0E0E0", bgcolor: "#FAFAFA" }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "#263238",
                    color: "white",
                    fontSize: 24,
                    fontWeight: 700
                  }}>
                  {getInitials(selectedEmployee?.firstName, selectedEmployee?.lastName)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedEmployee?.personalEmail || selectedEmployee?.PersonalEmail}
                  </Typography>
                </Box>
                <Chip
                  label={status ? "ACTIVE" : "INACTIVE"}
                  sx={{
                    fontWeight: 700,
                    bgcolor: status ? "#E8F5E9" : "#FFEBEE",
                    color: status ? "#2E7D32" : "#C62828"
                  }}
                />
              </Stack>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: "1px solid #E0E0E0", px: 3 }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 48 }}>
                <Tab
                  label="Details"
                  sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }}
                />
                <Tab label="Jobs" sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }} />
              </Tabs>
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
              {activeTab === 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, color: "text.secondary", fontWeight: 700 }}>
                    General Information
                  </Typography>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
                            label="Employee Code"
                            value={selectedEmployee?.employeeCode || selectedEmployee?.EmployeeCode}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="First Name"
                            value={selectedEmployee?.firstName || selectedEmployee?.FirstName}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Last Name"
                            value={selectedEmployee?.lastName || selectedEmployee?.LastName}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<EmailIcon sx={{ fontSize: 18 }} />}
                            label="Company Email"
                            value={selectedEmployee?.companyEmail || selectedEmployee?.CompanyEmail}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<EmailIcon sx={{ fontSize: 18 }} />}
                            label="Personal Email"
                            value={
                              selectedEmployee?.personalEmail || selectedEmployee?.PersonalEmail
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<SupervisorAccountIcon sx={{ fontSize: 18 }} />}
                            label="Supervisor"
                            value={selectedEmployee?.supervisorName || "N/A"}
                          />
                        </Grid>

                        {/* Address Section */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<PublicIcon sx={{ fontSize: 18 }} />}
                            label="Country"
                            value={selectedEmployee?.country || selectedEmployee?.Country}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<PublicIcon sx={{ fontSize: 18 }} />}
                            label="Province"
                            value={selectedEmployee?.province || selectedEmployee?.Province}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<PublicIcon sx={{ fontSize: 18 }} />}
                            label="City"
                            value={selectedEmployee?.city || selectedEmployee?.City}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetailRow
                            icon={<PublicIcon sx={{ fontSize: 18 }} />}
                            label="Last Used"
                            value="N/A"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search jobs..."
                    value={jobSearchTerm}
                    onChange={(e) => setJobSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: jobSearchTerm && (
                        <InputAdornment position="end">
                          <ClearIcon
                            sx={{ cursor: "pointer", fontSize: 18, color: "#9e9e9e" }}
                            onClick={() => setJobSearchTerm("")}
                          />
                        </InputAdornment>
                      )
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#F5F7FA" } }}
                  />

                  <Box sx={{ mt: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                    {allJobsLoading && allJobs.length === 0 ? (
                      <Stack alignItems="center" py={4}>
                        <CircularProgress size={24} />
                      </Stack>
                    ) : allJobs.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 3,
                          textAlign: "center",
                          bgcolor: "#FAFAFA",
                          borderStyle: "dashed"
                        }}>
                        <WorkIcon sx={{ fontSize: 32, color: "#BDBDBD", mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No jobs found
                        </Typography>
                      </Paper>
                    ) : (
                      <Stack
                        spacing={1}
                        ref={jobListRef}
                        onScroll={handleJobListScroll}
                        sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                        {allJobs.map((job) => {
                          const jobId = getJobId(job);
                          const jobName =
                            job?.clientJobName || job?.jobName || job?.name || "Untitled job";
                          const jobCode = job?.clientJobCode || job?.jobCode || job?.JobCode || "";
                          const assignmentRecord = employeeJobMap.get(jobId);
                          const assignmentId =
                            assignmentRecord?.clientEmployeeJobId ?? assignmentRecord?.id;
                          const isAssigned = Boolean(
                            assignmentRecord?.isSelected || assignmentId > 0
                          );
                          const switchDisabled = !selectedEmployee || jobToggleLoadingId === jobId;

                          return (
                            <Paper
                              key={jobId || job?.id}
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                p: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2
                              }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                sx={{ flex: 1, minWidth: 0 }}>
                                <Avatar sx={{ bgcolor: "#ECEFF1", width: 36, height: 36 }}>
                                  <BusinessCenterIcon sx={{ fontSize: 18, color: "#546E7A" }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}>
                                    {jobName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600 }}>
                                    {jobCode}
                                  </Typography>
                                </Box>
                              </Stack>
                              <ToggleSwitch
                                edge="end"
                                sx={{ mr: 1 }}
                                size="medium"
                                checked={isAssigned}
                                disabled={switchDisabled}
                                onChange={(event) => handleJobToggle(job, event.target.checked)}
                              />
                            </Paper>
                          );
                        })}
                        {isFetchingMoreJobs && (
                          <Box sx={{ py: 1.5, textAlign: "center" }}>
                            <CircularProgress size={18} />
                          </Box>
                        )}
                      </Stack>
                    )}

                    {!selectedEmployee && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                        Select an employee to manage job assignments.
                      </Typography>
                    )}

                    {employeeJobsLoading && selectedEmployee && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Refreshing assignments...
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Stack>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <PersonIcon sx={{ fontSize: 60, color: "#E0E0E0" }} />
            <Typography sx={{ mt: 2 }}>Select an employee to view details</Typography>
          </Stack>
        )}
      </Paper>

      {/* ========== ADD EMPLOYEE DIALOG ========== */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Add New Employee</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              autoFocus
              label="First Name"
              fullWidth
              required
              value={newEmployee.firstName}
              onChange={(e) => setNewEmployee({ ...newEmployee, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              required
              value={newEmployee.lastName}
              onChange={(e) => setNewEmployee({ ...newEmployee, lastName: e.target.value })}
            />
            <TextField
              label="Personal Email"
              fullWidth
              required
              type="email"
              value={newEmployee.personalEmail}
              onChange={(e) => setNewEmployee({ ...newEmployee, personalEmail: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateEmployee} disabled={savingEmployee}>
            {savingEmployee ? "Saving..." : "Save & Invite"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Employees;
